import React, { useState } from 'react';
import { Search, MapPin, Package, Calendar, Truck } from 'lucide-react';
import { API_BASE_URL } from '../config/apiConfig';
import { useToast } from '../context/ToastContext';

const TrackShipment = () => {
  const [trackingId, setTrackingId] = useState('');
  const [shipment, setShipment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { addToast } = useToast();

 // Find and replace your formatDate function with this:

const formatDate = (date) => {
  console.log('📅 Formatting date:', date, 'Type:', typeof date);
  
  if (!date) {
    return 'N/A';
  }

  try {
    let dateObj;

    // ✅ Handle Firestore Timestamp object { _seconds, _nanoseconds }
    if (date && typeof date === 'object' && '_seconds' in date) {
      dateObj = new Date(date._seconds * 1000);
    }
    // ✅ Handle Firestore Timestamp object { seconds, nanoseconds } (alternative format)
    else if (date && typeof date === 'object' && 'seconds' in date) {
      dateObj = new Date(date.seconds * 1000);
    }
    // ✅ Handle Firestore Timestamp with toDate() method
    else if (date && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    }
    // ✅ Handle ISO string or regular date string
    else if (typeof date === 'string') {
      dateObj = new Date(date);
    }
    // ✅ Handle milliseconds timestamp (number)
    else if (typeof date === 'number') {
      dateObj = new Date(date);
    }
    // ✅ Handle Date object directly
    else if (date instanceof Date) {
      dateObj = date;
    }
    else {
      console.warn('⚠️ Unknown date format:', date);
      return 'N/A';
    }

    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      console.error('❌ Invalid date after parsing:', date);
      return 'N/A';
    }

    // Format the date nicely
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    console.error('❌ Error formatting date:', error, date);
    return 'N/A';
  }
};

  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setShipment(null);

    if (!trackingId.trim()) {
      addToast('⚠️ Please enter a tracking ID', 'error');
      return;
    }

    setIsLoading(true);

    try {
      console.log('🔍 Searching for tracking ID:', trackingId);
      const response = await fetch(`${API_BASE_URL}/track/${trackingId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Shipment not found');
      }

      const data = await response.json();
      console.log('✅ Shipment found:', data.shipment);

      setShipment(data.shipment);
      addToast('✅ Shipment found!', 'success');
    } catch (error) {
      console.error('❌ Error:', error);
      setError(error.message);
      addToast(`❌ ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">🔍 Track Your Shipment</h1>
          <p className="text-slate-300 text-lg">Enter your tracking ID to monitor your package</p>
        </div>

        {/* Search Box */}
        <div className="bg-white rounded-lg shadow-2xl p-8 mb-8">
          <form onSubmit={handleSearch} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2">
                Tracking ID
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value)}
                  placeholder="e.g., ODI-ABCD1234-XYZ0"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg transition flex items-center justify-center gap-2 cursor-pointer"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Searching...
                </>
              ) : (
                <>
                  <Search className="h-5 w-5" /> Search
                </>
              )}
            </button>
          </form>
        </div>

        {/* Shipment Details */}
        {shipment && (
          <div className="space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-lg shadow-2xl p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Current Status */}
                <div>
                  <h2 className="text-sm font-semibold text-slate-600 mb-2">📦 Current Status</h2>
                  <p className={`text-3xl font-bold ${
                    shipment.status === 'Delivered' ? 'text-green-600' :
                    shipment.status === 'In Transit' ? 'text-blue-600' :
                    shipment.status === 'Out for Delivery' ? 'text-cyan-600' :
                    'text-yellow-600'
                  }`}>
                    {shipment.status}
                  </p>
                </div>

                {/* Current Location */}
                <div>
                  <h2 className="text-sm font-semibold text-slate-600 mb-2">📍 Current Location</h2>
                  <p className="text-2xl font-bold text-slate-900">{shipment.currentLoc || 'N/A'}</p>
                </div>

                {/* Destination */}
                <div>
                  <h2 className="text-sm font-semibold text-slate-600 mb-2">🗺️ Destination</h2>
                  <p className="text-lg font-semibold text-slate-900">{shipment.destination}</p>
                </div>

                {/* Weight */}
                <div>
                  <h2 className="text-sm font-semibold text-slate-600 mb-2">⚖️ Weight</h2>
                  <p className="text-lg font-semibold text-slate-900">{shipment.weight} kg</p>
                </div>
              </div>

              {/* Divider */}
              <hr className="my-6" />

              {/* Customer Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-slate-600 mb-1">👤 Customer Name</h3>
                  <p className="text-slate-900">{shipment.customerName}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-600 mb-1">📧 Email</h3>
                  <p className="text-slate-900">{shipment.email}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-600 mb-1">📱 Phone</h3>
                  <p className="text-slate-900">{shipment.phone || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-600 mb-1">📅 Created Date</h3>
                  <p className="text-slate-900 font-semibold">{formatDate(shipment.createdAt)}</p>
                </div>
              </div>
            </div>

            {/* Tracking History */}
            {shipment.history && shipment.history.length > 0 && (
              <div className="bg-white rounded-lg shadow-2xl p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">📋 Tracking History</h2>

                <div className="space-y-4">
                  {shipment.history.map((entry, index) => (
                    <div key={index} className="flex gap-4 pb-4 border-b last:border-b-0">
                      <div className="flex-shrink-0">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                          <Truck className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="flex-grow">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-slate-900">{entry.status || 'Update'}</h3>
                            <p className="text-sm text-slate-600 mt-1">{entry.text}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-slate-600">
                              {formatDate(entry.timestamp)}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">by {entry.by || 'System'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            {shipment.description && (
              <div className="bg-white rounded-lg shadow-2xl p-8">
                <h2 className="text-sm font-semibold text-slate-600 mb-2">📝 Package Description</h2>
                <p className="text-slate-900">{shipment.description}</p>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!shipment && !isLoading && !error && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 text-center mt-8">
            <p className="text-slate-300 text-sm">
              💡 <strong>Tip:</strong> Your tracking ID was sent to your email after booking
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackShipment;